import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { DEFAULT_PRICING_PLAN, DEFAULT_PAYG_PLAN, BILLING_MODE, BILLING_MODE_LABEL } from "../../../constants";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import parsePhoneNumberFromString, { parsePhoneNumberWithError } from "libphonenumber-js";
import {
  CompanyTextField,
  CompanyPricingField,
  CompanyInvoiceDayField,
  StepIndicator,
  validatePersonal,
  validateCompanyForm,
} from "./CompanyFormParts";
import { TABS, errorsByTab, firstErrorKey, firstTabWithError, focusField } from "./companyFieldTabs";
import { buildCreateSummary, formatCurrency } from "./companyChangeSet";

const STEP = { BASIC: "basic", BILLING: "billing" };

const STEPS = [
  { key: STEP.BASIC, label: "Basic details", icon: "fa fa-user" },
  { key: STEP.BILLING, label: "Pricing & Billing", icon: "fa fa-dollar-sign" },
];

const PERSONAL_ERROR_KEYS = ["username", "email", "phoneNo"];

// One factory for BOTH the initial state and the reset on close, so they can never drift apart.
//
// The reset MUST mirror the initial state exactly. Omitting billingMode/paygPlan once left
// formData.paygPlan undefined after the first close, so reopening and choosing Pay As You Go
// crashed on formData.paygPlan[field].
const buildInitialFormData = () => ({
  username: "",
  email: "",
  validPaymentMethodRequired: false,
  trialModeEnabled: true,
  legacyCompany: false,
  pricingPlan: { ...DEFAULT_PRICING_PLAN },
  billingMode: BILLING_MODE.SEAT,
  paygPlan: { ...DEFAULT_PAYG_PLAN },
  phoneNo: "",
  countryCode: "",
});

export default function AddCompany({ activeModal, setActiveModal, setUpdateModal }) {
  const [formData, setFormData] = useState(buildInitialFormData);
  const [step, setStep] = useState(STEP.BASIC);

  const [defaultPricing, setDefaultPricing] = useState(null);
  const [error, setError] = useState({});
  const [disable, setDisable] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  // Set on any user edit. The close guard uses this instead of diffing formData against its
  // initial value, because the default-pricing fetch legitimately rewrites pricingPlan/paygPlan
  // without the user having touched anything.
  const [touched, setTouched] = useState(false);

  const formRef = useRef(null);
  const bodyRef = useRef(null);
  const validateTimeoutRef = useRef(null);
  const inputRefs = useRef({});
  const refCallbacks = useRef({});

  // One stable ref callback per field name. A fresh function each render would make React
  // detach and re-attach the ref on every render.
  const setInputRef = useCallback((name) => {
    if (!refCallbacks.current[name]) {
      refCallbacks.current[name] = (element) => {
        if (element) {
          inputRefs.current[name] = element;
        } else {
          delete inputRefs.current[name];
        }
      };
    }
    return refCallbacks.current[name];
  }, []);

  // Debounced validation of the PERSONAL fields only. It MERGES into the error object: the old
  // version replaced the whole object ~1s after every change, which wiped pricing errors and the
  // "required" errors set on Next / Create.
  const debouncedValidate = useCallback(({ username, email, phoneNo, countryCode }) => {
    if (validateTimeoutRef.current) {
      clearTimeout(validateTimeoutRef.current);
    }

    validateTimeoutRef.current = setTimeout(() => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const trimmedUsername = username.trim();
      const trimmedEmail = email.trim();

      setError((prev) => {
        const next = { ...prev };

        // An empty username/email is left alone: an untouched field must not scream "required"
        // while the user is typing elsewhere, and a "required" error from Next/Create stays
        // until the user edits that field (handleInputChange clears it then).
        if (trimmedUsername) {
          if (trimmedUsername.length < 2) {
            next.username = "Username must be at least 2 characters.";
          } else {
            delete next.username;
          }
        }

        if (trimmedEmail) {
          if (!emailRegex.test(trimmedEmail)) {
            next.email = "Enter a valid email.";
          } else {
            delete next.email;
          }
        }

        // The phone is optional, so an empty one is always valid.
        if (phoneNo && countryCode) {
          const phoneNumber = parsePhoneNumberFromString(`${countryCode}${phoneNo}`);
          if (!phoneNumber || !phoneNumber.isValid()) {
            next.phoneNo = "Please enter a valid phone number";
          } else {
            delete next.phoneNo;
          }
        } else {
          delete next.phoneNo;
        }

        const changed = PERSONAL_ERROR_KEYS.some((key) => (prev[key] || "") !== (next[key] || ""));
        return changed ? next : prev;
      });
    }, 1000); // 1000ms debounce
  }, []);

  // Load default pricing configuration
  const loadDefaultPricing = useCallback(async () => {
    if (!activeModal || defaultPricing) return;

    setIsLoadingDefaults(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_base_url}api/system-config/default-pricing`, {
        headers: { auth_code: process.env.REACT_APP_AUTH_CODE }
      });

      if (response.data.success && response.data.data) {
        const fetchedPricing = response.data.data;
        setDefaultPricing(fetchedPricing);

        // The response now carries BOTH price sheets: the seat fields at the top level and
        // a nested `payg` block. Split them.
        //
        // Seeding paygPlan here is what makes the Pricing Config PAYG tab actually mean
        // something: without it a company was always created on the hardcoded constant, so
        // a Super Admin who set the default per-topo rate to $30 still got $25. That is a
        // money bug, not a cosmetic one.
        //
        // `payg` is also stripped out of pricingPlan - copying it in sends a nested payg
        // object to add-admin-stripe and blurs the exact pricingPlan / paygPlan separation
        // this feature is built around.
        const { payg: fetchedPayg, ...fetchedSeatPricing } = fetchedPricing;

        setFormData(prev => ({
          ...prev,
          pricingPlan: {
            ...DEFAULT_PRICING_PLAN,
            ...fetchedSeatPricing
          },
          paygPlan: {
            ...DEFAULT_PAYG_PLAN,
            ...(fetchedPayg || {})
          }
        }));
      } else {
        console.warn('Using fallback pricing - no system config found');
        setDefaultPricing(DEFAULT_PRICING_PLAN);
      }
    } catch (error) {
      console.error('Error loading default pricing:', error);
      toast.warning('Using fallback pricing configuration');
      setDefaultPricing(DEFAULT_PRICING_PLAN);
    } finally {
      setIsLoadingDefaults(false);
    }
  }, [activeModal, defaultPricing]);

  // Modal opens/closes. Kept separate from the defaults fetch below so that the fetch finishing
  // (which changes loadDefaultPricing) does not clear errors or reset the step.
  useEffect(() => {
    if (activeModal) {
      setError({});
      setDisable(false);
      setStep(STEP.BASIC);
      setTouched(false);
    } else {
      // Reset form when modal closes (see buildInitialFormData).
      setFormData(buildInitialFormData());
      setDefaultPricing(null);
      setStep(STEP.BASIC);
      setTouched(false);
      if (validateTimeoutRef.current) {
        clearTimeout(validateTimeoutRef.current);
      }
    }
  }, [activeModal]);

  // Defaults are (re)fetched every time the modal opens: the reset above nulls defaultPricing.
  useEffect(() => {
    if (activeModal) {
      loadDefaultPricing();
    }
  }, [activeModal, loadDefaultPricing]);

  // Each step starts at the top of the scrolling modal body.
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  }, [step]);

  // Handle phone number changes - memoized
  const handlePhoneChange = useCallback((value) => {
    if (!value) {
      // Only a real edit (clearing an existing number) counts as touched.
      if (formData.phoneNo) setTouched(true);
      setFormData(prev => ({
        ...prev,
        countryCode: "",
        phoneNo: "",
      }));
      return;
    }

    try {
      const parsed = parsePhoneNumberWithError(value);
      setTouched(true);
      setFormData(prev => ({
        ...prev,
        countryCode: `+${parsed.countryCallingCode}`,
        phoneNo: parsed.nationalNumber,
      }));
    } catch (err) {
      console.error("Invalid phone number:", err);
    }
  }, [formData.phoneNo]);

  // Handle form input changes. The fields are module-level components, so they keep focus
  // across re-renders on their own (no caret-restore workaround needed).
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setTouched(true);

    if (name.startsWith('pricingPlan.')) {
      const pricingKey = name.replace('pricingPlan.', '');
      setFormData(prev => ({
        ...prev,
        pricingPlan: {
          ...prev.pricingPlan,
          [pricingKey]: type === 'number' ? Math.max(0, Number(value)) : value
        }
      }));
    } else if (name.startsWith('paygPlan.')) {
      // Without this branch the value would be written as a FLAT key
      // (formData['paygPlan.pricePerTopo']) and the nested paygPlan object would never
      // update - the input would appear frozen.
      const paygKey = name.replace('paygPlan.', '');
      setFormData(prev => ({
        ...prev,
        paygPlan: {
          ...prev.paygPlan,
          [paygKey]: type === 'number' || paygKey === 'invoiceDayOfMonth'
            ? Math.max(0, Number(value))
            : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear specific error for this field immediately
    setError(prev => (prev[name] ? { ...prev, [name]: '' } : prev));
  }, []);

  // Billing model toggle. The two price sheets are never mixed, so an error left on the sheet
  // that is being hidden is dropped (otherwise it would keep a red dot on a step with no
  // visible problem).
  const handleBillingModeChange = useCallback((mode) => {
    setTouched(true);
    setFormData(prev => ({
      ...prev,
      billingMode: mode,
      // PAYG has no manual-billing option: a standalone invoice charges the card on file, so
      // auto-pay is forced on and locked. Switching back to SEAT leaves it as it is.
      ...(mode === BILLING_MODE.PAYG ? { validPaymentMethodRequired: true } : {}),
    }));
    setError(prev => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (!key.startsWith('pricingPlan.') && !key.startsWith('paygPlan.')) next[key] = prev[key];
      });
      return next;
    });
  }, []);

  // Handle legacy company toggle - optimized
  const handleLegacyToggle = useCallback((e) => {
    const isLegacy = e.target.checked;
    setTouched(true);

    setFormData(prev => ({
      ...prev,
      legacyCompany: isLegacy,
      pricingPlan: {
        ...prev.pricingPlan,
        foundationAnalysisAddOn: isLegacy ? 0 : (defaultPricing?.foundationAnalysisAddOn || DEFAULT_PRICING_PLAN.foundationAnalysisAddOn)
      }
    }));

    if (isLegacy) {
      toast.info('Legacy company status enabled. Foundation Analysis add-on is now FREE.');
    }
  }, [defaultPricing]);

  // Reset to default pricing - memoized
  const handleResetToDefault = useCallback(() => {
    if (defaultPricing) {
      const { payg: defaultPayg, ...defaultSeatPricing } = defaultPricing;
      const adjustedPricing = { ...defaultSeatPricing };

      if (formData.legacyCompany) {
        adjustedPricing.foundationAnalysisAddOn = 0;
      }

      setTouched(true);
      setFormData(prev => ({
        ...prev,
        pricingPlan: adjustedPricing,
        // Reset whichever price sheet the form is currently showing.
        paygPlan: { ...DEFAULT_PAYG_PLAN, ...(defaultPayg || {}) },
      }));
      toast.info('Pricing reset to system defaults with legacy adjustments');
    }
  }, [defaultPricing, formData.legacyCompany]);

  // Close guard: Cancel, the header X and Esc all come through here.
  const handleClose = useCallback(() => {
    if (touched && !window.confirm('You have unsaved changes. Close anyway?')) {
      return;
    }
    setActiveModal();
  }, [touched, setActiveModal]);

  // Step 1 -> 2. Validates the personal fields only.
  const handleNext = useCallback(() => {
    const errs = validatePersonal({
      username: formData.username,
      email: formData.email,
      phoneNo: formData.phoneNo,
      countryCode: formData.countryCode,
    });

    if (Object.keys(errs).length > 0) {
      setError(prev => ({ ...prev, ...errs }));
      toast.error('Please fix the highlighted fields');
      focusField(inputRefs, firstErrorKey(errs, TABS.PERSONAL));
      return;
    }

    setStep(STEP.BILLING);
  }, [formData.username, formData.email, formData.phoneNo, formData.countryCode]);

  // Submit handler - validates everything, and jumps to the first step that has an error.
  const handleSubmit = useCallback(async () => {
    const errs = validateCompanyForm(formData, formData.billingMode);

    if (Object.keys(errs).length > 0) {
      setError(errs);
      toast.error('Please fix the highlighted fields');

      const errorTab = firstTabWithError(errs);
      setStep(errorTab === TABS.PERSONAL ? STEP.BASIC : STEP.BILLING);
      // focusField waits a frame, by which time the step's inputs are mounted.
      focusField(inputRefs, firstErrorKey(errs, errorTab));
      return;
    }

    setDisable(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_base_url}api/masterAdmin/add-admin-stripe`,
        {
          auth_code: process.env.REACT_APP_AUTH_CODE,
          username: formData.username.trim(),
          email: formData.email.trim(),
          validPaymentMethodRequired: formData.validPaymentMethodRequired,
          trialModeEnabled: formData.trialModeEnabled,
          legacyCompany: formData.legacyCompany,
          pricingPlan: formData.pricingPlan,
          billingMode: formData.billingMode,
          paygPlan: formData.paygPlan,
          phoneNo: formData.phoneNo,
          countryCode: formData.countryCode,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.data.success) {
        toast.success("Company added successfully!");
        setActiveModal();
        setUpdateModal();
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong!");
    } finally {
      setDisable(false);
    }
  }, [formData, setActiveModal, setUpdateModal]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validateTimeoutRef.current) {
        clearTimeout(validateTimeoutRef.current);
      }
    };
  }, []);

  // Trigger debounced validation of the personal fields when they change
  const { username, email, phoneNo, countryCode } = formData;
  useEffect(() => {
    debouncedValidate({ username, email, phoneNo, countryCode });
  }, [username, email, phoneNo, countryCode, debouncedValidate]);

  const isPayg = formData.billingMode === BILLING_MODE.PAYG;
  const stepErrors = errorsByTab(error);
  const hasStepError = { [STEP.BASIC]: stepErrors[TABS.PERSONAL], [STEP.BILLING]: stepErrors[TABS.BILLING] };
  const summaryLines = buildCreateSummary(formData, { billingMode: formData.billingMode });
  const previewCost = formData.pricingPlan.baseCharge + formData.pricingPlan.companyUser;

  return (
    <Modal show={activeModal} onHide={handleClose} size="xl" backdrop="static" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-plus me-2"></i>
          Add Company
          {formData.legacyCompany && (
            <span className="badge bg-warning ms-2">
              <i className="fa fa-star me-1 mr-2"></i>Legacy Company
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      {/* Outside Modal.Body so the indicator stays put while a long step scrolls. */}
      <div className="px-3 pt-3 flex-shrink-0">
        <StepIndicator
          steps={STEPS}
          current={step}
          hasError={hasStepError}
          // Going back is always fine; going forward only happens through Next (which validates).
          onStepClick={step === STEP.BILLING ? (key) => { if (key === STEP.BASIC) setStep(STEP.BASIC); } : undefined}
        />
      </div>

      <Modal.Body ref={bodyRef}>
        {isLoadingDefaults && (
          <div className="alert alert-info">
            <i className="fa fa-spinner fa-spin me-2 mr-2"></i>
            Loading default pricing configuration...
          </div>
        )}

        <form ref={formRef} onSubmit={(e) => e.preventDefault()}>
          {step === STEP.BASIC && (
            <div className="cmp-tab-pane">
              {/* User Details Section */}
              <fieldset className="border rounded p-3 mb-3">
                <legend className="w-auto px-2">USER DETAILS</legend>

                <div className="row ml-1">
                  <div className="col-md-6">
                    <CompanyTextField
                      label="Username"
                      name="username"
                      value={formData.username}
                      error={error.username}
                      onChange={handleInputChange}
                      inputRef={setInputRef('username')}
                    />
                  </div>
                  <div className="col-md-6">
                    <CompanyTextField
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      error={error.email}
                      onChange={handleInputChange}
                      inputRef={setInputRef('email')}
                    />
                  </div>
                </div>

                <div className="row ml-1">
                  <div className="col-md-12">
                    <CompanyTextField label="Phone Number" name="phoneNo" required={false}>
                      <PhoneInput
                        ref={setInputRef('phoneNo')}
                        placeholder="Enter phone number"
                        value={`${formData.countryCode}${formData.phoneNo}`}
                        onChange={handlePhoneChange}
                        defaultCountry="US"
                        className={error.phoneNo ? 'is-invalid' : ''}
                      />
                      {error.phoneNo && <div className="invalid-feedback d-block">{error.phoneNo}</div>}
                    </CompanyTextField>
                  </div>
                </div>
              </fieldset>
            </div>
          )}

          {step === STEP.BILLING && (
            <div className="cmp-tab-pane">
              {/* Billing settings (moved here from the old Company Settings block) */}
              <fieldset className="border rounded p-3 mb-3">
                <legend className="w-auto px-2">BILLING SETTINGS</legend>

                <div className="row ml-1">
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="validPaymentMethodRequired"
                        checked={formData.validPaymentMethodRequired}
                        onChange={handleInputChange}
                        disabled={isPayg}
                      />
                      <label className="ml-2 form-check-label">
                        <i className="fa fa-credit-card me-1 mr-2"></i>
                        Enable Auto Pay
                      </label>
                      {isPayg && (
                        <div className="form-text text-muted">
                          Required for Pay As You Go — invoices are charged to the card on file.
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="trialModeEnabled"
                        checked={formData.trialModeEnabled}
                        onChange={handleInputChange}
                      />
                      <label className="ml-2 form-check-label">
                        <i className="fa fa-clock me-1 mr-2"></i>
                        Trial Period
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="legacyCompany"
                        checked={formData.legacyCompany}
                        onChange={handleLegacyToggle}
                      />
                      <label className="ml-2 form-check-label">
                        <i className="fa fa-star me-1 mr-2"></i>
                        Legacy Company Status
                        <small className="d-block text-muted">
                          Grants free Foundation Analysis add-on
                        </small>
                      </label>
                    </div>
                  </div>
                </div>
              </fieldset>

              {/* Pricing Configuration */}
              <fieldset className="border rounded p-3 mb-3">
                <legend className="w-auto px-2">
                  PRICING CONFIGURATION
                  {defaultPricing && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary ms-2 ml-2"
                      onClick={handleResetToDefault}
                      title="Reset to system defaults"
                    >
                      <i className="fa fa-undo"></i> Reset to Default
                    </button>
                  )}
                </legend>

                {/* Which model bills this company. Swaps the entire field set below, because
                    a per-seat price sheet and a per-topo price sheet share nothing. */}
                <div className="mb-3">
                  <label className="form-label d-block">Billing Model</label>
                  {/* Both states share the same class family (outline + `active`), so the two
                      buttons are exactly the same size - a solid `.btn-primary` loses its border
                      under the PricingConfig.css override and rendered ~2px smaller. */}
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn btn-outline-primary ${formData.billingMode === BILLING_MODE.SEAT ? 'active' : ''}`}
                      onClick={() => handleBillingModeChange(BILLING_MODE.SEAT)}
                    >
                      <i className="fa fa-users mr-2"></i>{BILLING_MODE_LABEL.SEAT}
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-primary ${formData.billingMode === BILLING_MODE.PAYG ? 'active' : ''}`}
                      onClick={() => handleBillingModeChange(BILLING_MODE.PAYG)}
                    >
                      <i className="fa fa-bolt mr-2"></i>{BILLING_MODE_LABEL.PAYG}
                    </button>
                  </div>
                </div>

                {isPayg ? (
                  <>
                    <div className="alert alert-info">
                      <i className="fa fa-info-circle mr-2"></i>
                      Charged for the <strong>topos created</strong>, not the users employed. Unlimited users of any
                      type are never charged, and Foundation Analysis and Floor Level are included for every eligible
                      user. A card is required - Pay As You Go has no manual-billing option.
                    </div>

                    <h6 className="fw-bold mt-3 mb-2 ml-3">
                      <i className="fa fa-calendar-alt me-1 mr-2"></i>
                      Monthly Base
                    </h6>
                    <div className="row">
                      <CompanyPricingField
                        label="Base Price (USD / month)"
                        name="paygPlan.baseCharge"
                        value={formData.paygPlan?.baseCharge ?? 0}
                        error={error['paygPlan.baseCharge']}
                        onChange={handleInputChange}
                        inputRef={setInputRef('paygPlan.baseCharge')}
                        hint="May be 0"
                      />
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Currency</label>
                          <select className="form-control" value="usd" disabled readOnly>
                            <option value="usd">USD ($)</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <h6 className="fw-bold mt-3 mb-2 ml-3">
                      <i className="fa fa-bolt me-1 mr-2"></i>
                      Per-Topo Pricing
                    </h6>
                    <div className="row">
                      <CompanyPricingField
                        label="Per Topo Charge (USD)"
                        name="paygPlan.pricePerTopo"
                        value={formData.paygPlan?.pricePerTopo ?? 0}
                        error={error['paygPlan.pricePerTopo']}
                        onChange={handleInputChange}
                        inputRef={setInputRef('paygPlan.pricePerTopo')}
                        hint="May be 0 for a free / pilot company"
                      />
                      <CompanyInvoiceDayField
                        value={formData.paygPlan?.invoiceDayOfMonth ?? 1}
                        error={error['paygPlan.invoiceDayOfMonth']}
                        onChange={handleInputChange}
                        inputRef={setInputRef('paygPlan.invoiceDayOfMonth')}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="row">
                      <CompanyPricingField
                        label="Base Charge (USD)"
                        name="pricingPlan.baseCharge"
                        value={formData.pricingPlan.baseCharge}
                        error={error['pricingPlan.baseCharge']}
                        onChange={handleInputChange}
                        inputRef={setInputRef('pricingPlan.baseCharge')}
                      />
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Currency</label>
                          <select
                            className="form-control"
                            name="pricingPlan.currency"
                            value={formData.pricingPlan.currency}
                            onChange={handleInputChange}
                          >
                            <option value="usd">USD ($)</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <h6 className="fw-bold mt-3 mb-2 ml-3">
                      <i className="fa fa-users me-1"></i>
                      User Type Pricing (USD)
                    </h6>
                    <div className="row ml-1">
                      <CompanyPricingField
                        label="Company User"
                        name="pricingPlan.companyUser"
                        value={formData.pricingPlan.companyUser}
                        error={error['pricingPlan.companyUser']}
                        onChange={handleInputChange}
                        inputRef={setInputRef('pricingPlan.companyUser')}
                      />
                      <CompanyPricingField
                        label="Read Write User"
                        name="pricingPlan.readWrite"
                        value={formData.pricingPlan.readWrite}
                        error={error['pricingPlan.readWrite']}
                        onChange={handleInputChange}
                        inputRef={setInputRef('pricingPlan.readWrite')}
                      />
                      <CompanyPricingField
                        label="Topo User (seat)"
                        name="pricingPlan.topo"
                        value={formData.pricingPlan.topo}
                        error={error['pricingPlan.topo']}
                        onChange={handleInputChange}
                        inputRef={setInputRef('pricingPlan.topo')}
                      />
                      <CompanyPricingField
                        label="Read Only User"
                        name="pricingPlan.readOnly"
                        value={formData.pricingPlan.readOnly}
                        error={error['pricingPlan.readOnly']}
                        onChange={handleInputChange}
                        inputRef={setInputRef('pricingPlan.readOnly')}
                      />
                    </div>

                    <h6 className="fw-bold mt-3 mb-2 ml-3">
                      <i className="fa fa-puzzle-piece me-1 mr-2"></i>
                      Add-On Pricing (USD)
                    </h6>
                    <div className="row ml-1">
                      <CompanyPricingField
                        label="Foundation Analysis Add-On"
                        name="pricingPlan.foundationAnalysisAddOn"
                        value={formData.pricingPlan.foundationAnalysisAddOn}
                        error={error['pricingPlan.foundationAnalysisAddOn']}
                        onChange={handleInputChange}
                        inputRef={setInputRef('pricingPlan.foundationAnalysisAddOn')}
                        isAddOn={true}
                        isLegacyFree={formData.legacyCompany}
                      />
                      <CompanyPricingField
                        label="Floor Level Add-On"
                        name="pricingPlan.floorLevelAddOn"
                        value={formData.pricingPlan.floorLevelAddOn}
                        error={error['pricingPlan.floorLevelAddOn']}
                        onChange={handleInputChange}
                        inputRef={setInputRef('pricingPlan.floorLevelAddOn')}
                        isAddOn={true}
                      />
                    </div>

                    {formData.legacyCompany && (
                      <div className="alert alert-info mt-3">
                        <i className="fa fa-lightbulb me-2 mr-1"></i>
                        <strong>Legacy Pricing Note:</strong> Foundation Analysis add-on is automatically set to $0 for legacy companies and cannot be changed.
                      </div>
                    )}
                  </>
                )}
              </fieldset>

              {/* Preview - SEAT ONLY. Under PAYG users are never charged, so a
                  "Monthly Cost (1 Company User)" figure directly contradicts the notice above it. */}
              {!isPayg && (
                <div className="alert alert-light border">
                  <h6 className="fw-bold">
                    <i className="fa fa-calculator me-2 mr-2"></i>
                    Preview: Monthly Cost (1 Company User)
                  </h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between">
                        <span>Base Charge + Company User:</span>
                        <span className="fw-bold">{formatCurrency(previewCost)}</span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between">
                        <span>Add-Ons Available:</span>
                        <span>
                          <span className="badge bg-primary me-1 mr-2">
                            Foundation ({formData.legacyCompany ? 'FREE' : formatCurrency(formData.pricingPlan.foundationAnalysisAddOn)})
                          </span>
                          <span className="badge bg-info">
                            Floor Level ({formatCurrency(formData.pricingPlan.floorLevelAddOn)})
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* What will happen */}
              <div className="cmp-summary">
                <h6 className="font-weight-bold mb-2">
                  <i className="fa fa-info-circle mr-2"></i>
                  What will happen when you create this company
                </h6>
                <ul>
                  {summaryLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleClose}
          disabled={disable}
        >
          Cancel
        </button>

        {step === STEP.BASIC ? (
          <button
            type="button"
            className="btn btn-outline-primary ml-2"
            onClick={handleNext}
          >
            Next
            <i className="fa fa-arrow-right ml-2"></i>
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-outline-secondary ml-2"
              onClick={() => setStep(STEP.BASIC)}
              disabled={disable}
            >
              <i className="fa fa-arrow-left mr-2"></i>
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary ml-2"
              onClick={handleSubmit}
              disabled={disable || isLoadingDefaults}
            >
              {disable ? (
                <>
                  <i className="fa fa-spinner fa-spin me-2 mr-2"></i>
                  Creating Company...
                </>
              ) : (
                <>
                  <i className="fa fa-save me-2 mr-2"></i>
                  Create Company
                  {formData.legacyCompany && <i className="fa fa-star ml-2 ms-1 text-warning"></i>}
                </>
              )}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
